import React from 'react'
import styled from 'styled-components'

import {Text, text} from '@/components/text'
import {InfoTooltip} from '@/components/tooltip/info-tooltip'
import {VisuallyHidden} from '@/components/visually-hidden'
import checkIcon from '@/images/icons/check-green-circle.svg'
import minusCircle from '@/images/icons/minus-circle.svg'
import mq from '@/utils/media-query'

import type {TierTypes} from '@/components/pricing'
import type {PricingComparisonTableFragment} from '@/graphql-types'

interface PricingComparisonTableProps {
    content: PricingComparisonTableFragment['data']
    comparedTiers: TierTypes[]
}

export const PricingComparisonTable = ({content, comparedTiers}: PricingComparisonTableProps) => {
    const renderTableCellContent = (
        feature: PricingComparisonTableProps['content']['features'][number],
        tier: TierTypes,
    ) => {
        const excerpt = feature[`${tier}_excerpt`]
        const tier1 = feature[`${tier}1`]

        if (excerpt) {
            return excerpt
        }

        return tier1 ? <img src={checkIcon} alt="" /> : <img src={minusCircle} alt="" />
    }

    return (
        <Table>
            <Caption $variant="paragraph-large-medium" as="caption">
                {content.title}
            </Caption>

            <THead role="presentation">
                <TR>
                    {comparedTiers.map((tier) => (
                        <TH key={`presentation-${tier}`} scope="col" id={tier}>
                            <VisuallyHidden>{tier} plan</VisuallyHidden>
                        </TH>
                    ))}
                </TR>
            </THead>

            <TBody>
                {content.features.map((feature, index) => {
                    const nameSlug = feature.name.toLowerCase().replace(/\s/g, '-')

                    return (
                        <TR key={`${feature.name}-${index}`}>
                            <TH scope="row" id={nameSlug}>
                                <THContent>
                                    {feature.name}
                                    {feature.tooltip_description && (
                                        <InfoTooltip
                                            closeOnScroll
                                            label={feature.tooltip_description}
                                            side="bottom"
                                        />
                                    )}
                                </THContent>
                            </TH>

                            {comparedTiers.map((tier) => (
                                <TD key={tier} headers={`${nameSlug} ${tier}`}>
                                    {renderTableCellContent(feature, tier)}
                                </TD>
                            ))}
                        </TR>
                    )
                })}
            </TBody>
        </Table>
    )
}

const Caption = styled(Text)`
    background-color: ${({theme}) => theme.colors.background.default};
    padding: ${({theme}) => `${theme.spacings.s20} ${theme.spacings.s24} 22px`};
    border-bottom: ${({theme}) => theme.borders.default};

    ${mq.min('sm')} {
        border-top: none;
        padding: ${({theme}) => theme.spacings.s20};
    }
`

const Table = styled.table`
    background-color: ${({theme}) => theme.colors.background.default};
    width: 100%;
    table-layout: fixed;
    border-spacing: 0;
    border-collapse: collapse;

    caption {
        text-align: left;
    }
`

const THead = styled.thead`
    padding: ${({theme}) => theme.spacings.s24};

    ${text({$variant: 'paragraph-base', $color: 'muted'})}
`

const TFoot = styled.tfoot`
    ${text({$variant: 'title-base'})}
`

const TBody = styled.tbody`
    & tr:last-child {
        padding-bottom: ${({theme}) => theme.spacings.s20};

        ${mq.min('sm')} {
            padding-bottom: 0;
        }
    }

    ${mq.min('sm')} {
        border-bottom: ${({theme}) => theme.borders.default};
    }
`

const TR = styled.tr`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    border-bottom: ${({theme}) => theme.borders.default};

    &:last-child {
        border-bottom: none;
    }

    ${mq.min('sm')} {
        display: flex;
    }
`

const TH = styled.th`
    padding: 0 ${({theme}) => theme.spacings.s24};
    grid-column: 1 / 3;

    ${mq.min('sm')} {
        flex-basis: 40%;
        display: flex;
    }

    ${mq.min('md')} {
        flex-basis: 30%;
    }

    ${THead} & {
        border: none;
    }
`

const THContent = styled.div`
    display: flex;
    justify-content: space-between;
    padding-top: ${({theme}) => theme.spacings.s12};
    text-align: left;

    ${text({$variant: 'label-base-medium', $color: 'muted'})}

    & > * {
        flex-shrink: 0;
    }

    ${mq.min('sm')} {
        padding: 0;
        flex-basis: 100%;
        align-items: center;
    }
`

const TD = styled.td`
    text-align: center;
    text-wrap: pretty;

    tbody & {
        padding: ${({theme}) =>
            `${theme.spacings.s16} ${theme.spacings.s24} ${theme.spacings.s12}`};
        ${text({$variant: 'label-base'})}

        ${mq.min('sm')} {
            padding: 22px ${({theme}) => ` ${theme.spacings.s20}`};
        }
    }

    &:first-of-type {
        border-left: ${({theme}) => theme.borders.default};
    }

    ${TFoot} & {
        padding-top: ${({theme}) => theme.spacings.s24};
    }

    img {
        display: inline;
    }

    ${mq.min('sm')} {
        flex-basis: 30%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    ${mq.min('md')} {
        flex-basis: 17.5%;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`
